/** VPN-419 view contract. Counts are numbers; byte amounts are decimal strings. */
export type DataStatus = 'complete' | 'partial' | 'not_collected' | 'not_matured' | 'source_error';

export interface DataValue<T> {
  value: T | null;
  status: DataStatus;
  asOf: string | null;
  coverageStart: string | null;
  reason: string | null;
  sources?: SourceCoverage[];
}

export class OperationsApiError extends Error {
  constructor(message: string, readonly httpStatus: number) {
    super(message);
    this.name = 'OperationsApiError';
  }
}

/** A projection revision can advance while an immutable snapshot is built. */
export async function retryProjectionConflict<T>(
  request: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (
      signal?.aborted ||
      !(error instanceof OperationsApiError) ||
      error.httpStatus !== 409 ||
      !error.message.startsWith('營運投影已更新，請重試')
    ) throw error;
  }
  return request();
}

export interface SourceCoverage {
  brand?: string | null;
  date?: string | null;
  nodeId: number | null;
  protocol: string | null;
  status: DataStatus | 'stale';
  asOf: string | null;
  reason: string | null;
}

export interface OperationKpi {
  current: DataValue<number>;
  prior?: DataValue<number> | null;
  changeRate?: DataValue<number> | null;
}

export interface SummaryKpis {
  newMembers: OperationKpi;
  sameDayRedeemers: OperationKpi;
  sameDayRedemptionRate: OperationKpi;
  observedPeakMembers: OperationKpi;
  apkMembers: OperationKpi;
  apkShare: OperationKpi;
  windowsMembers: OperationKpi;
  windowsShare: OperationKpi;
}

export interface DailyRow {
  date: string;
  newMembers: DataValue<number>;
  sameDayRedeemers: DataValue<number>;
  observedPeakMembers: DataValue<number>;
  observedPeakDevices: DataValue<number>;
  apkMembers: DataValue<number>;
  windowsMembers: DataValue<number>;
  totalUseMembers: DataValue<number>;
  apkDevices: DataValue<number>;
  windowsDevices: DataValue<number>;
}

/** Keep the primary trend aligned with the selected period, including custom ranges. */
export function operationsTrendForRange(
  range: { from: string; through: string },
  today: string,
  daily: DailyRow[] = [],
): { title: string; rows: DailyRow[] } {
  const days = Math.round((Date.parse(`${range.through}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / 86_400_000) + 1;
  return {
    title: range.through === today ? `近 ${days} 天關鍵指標趨勢` : `${range.from} ～ ${range.through} 關鍵指標趨勢`,
    rows: daily,
  };
}

export interface CohortRow {
  registrationDay: string;
  registrations: DataValue<number>;
  sameDayRedeemers: DataValue<number>;
  sameDayRate: DataValue<number>;
  cumulativeRedeemers: DataValue<number>;
  cumulativeRate: DataValue<number>;
  cumulativeUnredeemed: DataValue<number>;
  successfulRedemptionRecords: DataValue<number>;
  postRedemptionUseMembers: DataValue<number>;
  postRedemptionUseRate: DataValue<number>;
}

export interface ActivityDayBucket {
  label: string;
  members: DataValue<number>;
  ratio: DataValue<number>;
}

export interface OperationsSummary {
  snapshotId: string;
  cutoffAt: string;
  timezone: 'Asia/Taipei';
  brands: string[];
  from: string;
  through: string;
  projectionVersion: string | number;
  coverage: SourceCoverage[];
  kpis: SummaryKpis;
  daily: DailyRow[];
  cohorts: CohortRow[];
  platformDistribution: Record<'apkOnly' | 'windowsOnly' | 'both' | 'otherUnknown', DataValue<number>>;
  activityDayBuckets: ActivityDayBucket[];
}

export interface KshieldLegacyDaily {
  date: string;
  appReportedAccounts: number | null;
  appReportedDevices: number | null;
  appReportedAccountsByPlatform: Record<'apk' | 'windows' | 'other' | 'unknown', number> | null;
  appReportedDevicesByPlatform: Record<'apk' | 'windows' | 'other' | 'unknown', number> | null;
  receivedBytes: string | null;
  appConnectRows: number;
  bandwidthReportRows: number;
  reportingAccounts: number | null;
}

export interface KshieldLegacyObservation {
  brand: 'kshield'; from: string; through: string; timezone: 'Asia/Taipei'; cutoffAt: string;
  sourceCoverage: 'unverified'; sourceNotes: string[];
  daily: KshieldLegacyDaily[];
  sources: {
    appConnectReport: { observedRows: number; latestAt: string | null };
    legacyBandwidthReceipt: { observedRows: number; latestAt: string | null; excludedInvalidRows: number };
  };
  recentAppHeartbeats: { value: number | null; asOf: string; status: 'observed' | 'not_collected' | 'source_error' };
  ranking: { periodReportingAccounts: number; rows: Array<{ rank: number; accountLabel: string; receivedBytes: string; reportRows: number }> };
}

export interface OperationsOnline {
  estimated: true;
  asOf: string | null;
  sampleIntervalSec: number;
  activityWindowSec: number;
  members: DataValue<number>;
  devices: DataValue<number>;
  apkMembers: DataValue<number>;
  apkDevices: DataValue<number>;
  windowsMembers: DataValue<number>;
  windowsDevices: DataValue<number>;
  unknownMembers: DataValue<number>;
  unknownDevices: DataValue<number>;
  unattributedFlows: number | null;
  coverage: SourceCoverage[];
}

export interface TopAccountRow {
  rank: number;
  brand: string;
  memberAccountId: number;
  accountLabel: string;
  deviceCount: DataValue<number>;
  totalBytes: string | null;
  apkBytes: string | null;
  windowsBytes: string | null;
  otherBytes: string | null;
  unknownBytes: string | null;
  estimatedActive: DataValue<boolean>;
  lastConnectedAt: string | null;
}

export interface TopAccountsPage {
  snapshotId: string;
  cutoffAt: string;
  total: number;
  rows: TopAccountRow[];
  coverage: SourceCoverage[];
}

export interface MemberListRow {
  brand: string;
  memberAccountId: number;
  accountLabel: string;
  deviceCount?: DataValue<number>;
  totalBytes?: string | null;
}

export interface RedemptionRow extends MemberListRow {
  entitlementId: number;
  issuedAt: string;
}

export interface DeviceUsageRow {
  deviceLabel: string;
  platform: 'apk' | 'windows' | 'other' | 'unknown';
  uploadBytes: string | null;
  downloadBytes: string | null;
  totalBytes: string | null;
}

export interface SnapshotList<T> {
  snapshotId: string;
  cutoffAt: string;
  total: number;
  rows: T[];
  nextCursor?: string | null;
  coverage?: SourceCoverage[];
}

export function formatMetric(value: DataValue<number | boolean> | null | undefined, suffix = ''): string {
  if (!value || value.value === null || value.value === undefined) return '—';
  if (typeof value.value === 'boolean') return value.value ? '是' : '否';
  if (!Number.isFinite(value.value)) return '—';
  return `${new Intl.NumberFormat('zh-TW', { maximumFractionDigits: 2 }).format(value.value)}${suffix}`;
}

/** The leaderboard contains only usage with a verified canonical member. */
export function formatTopAccountCount(total: number | null | undefined): string {
  return total == null ? '目前可歸屬會員：—' : `目前可歸屬會員：${total} 名`;
}

export function formatDecimalGb(bytes: string | null | undefined): string {
  if (bytes === null || bytes === undefined || !/^\d+$/.test(bytes)) return '—';
  const value = BigInt(bytes);
  const hundredths = (value * 100n + 500_000_000n) / 1_000_000_000n;
  if (value > 0n && hundredths === 0n) return '<0.01 GB';
  return `${(hundredths / 100n).toString()}.${(hundredths % 100n).toString().padStart(2, '0')} GB`;
}

export function visiblePoints(values: Array<Pick<DataValue<number>, 'value' | 'status'>>): number[] {
  return values.flatMap(item => item.value !== null && Number.isFinite(item.value) ? [item.value] : []);
}

/** Readable 1/2/5 step with zero baseline; same-unit series pass one joined array. */
export function niceCountAxis(values: number[]): number[] {
  const maximum = Math.max(0, ...values.filter(Number.isFinite));
  if (maximum === 0) return [0, 1];
  const rough = maximum / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = Math.max(1, [1, 2, 5, 10].map(n => n * magnitude).find(n => n >= rough) ?? magnitude * 10);
  const ceiling = Math.ceil(maximum / step) * step;
  return Array.from({ length: Math.round(ceiling / step) + 1 }, (_, i) => Number((i * step).toPrecision(12)));
}

export class LatestScopeRequest {
  private generation = 0;
  private key = '';

  begin(key: string): { generation: number; key: string } {
    this.generation += 1;
    this.key = key;
    return { generation: this.generation, key };
  }

  invalidate(): void { this.generation += 1; this.key = ''; }

  isCurrent(request: { generation: number; key: string }, key: string): boolean {
    return request.generation === this.generation && request.key === key && key === this.key;
  }
}

function taipeiDate(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function taipeiWindow(now: Date, days: number): { from: string; through: string } {
  const through = taipeiDate(now);
  const anchor = new Date(`${through}T00:00:00.000Z`);
  anchor.setUTCDate(anchor.getUTCDate() - Math.max(1, days) + 1);
  return { from: anchor.toISOString().slice(0, 10), through };
}

export function topAccountsPath(brand: string, page: number, pageSize: number, snapshotId?: string | null): string {
  const params = new URLSearchParams({ brand, page: String(page), pageSize: String(pageSize) });
  if (snapshotId) params.set('snapshotId', snapshotId);
  return `/api/admin/operations/top-accounts?${params.toString()}`;
}

export function periodScopeKey(brand: string, from: string, through: string, refresh: number): string {
  return `${brand}:${from}:${through}:${refresh}`;
}

export function liveScopeKey(brand: string, refresh: number): string {
  return `${brand}:${refresh}`;
}

export function leaderboardScopeKey(brand: string, page: number, snapshotId: string | null, refresh: number): string {
  return `${brand}:${page}:${snapshotId ?? 'latest'}:${refresh}`;
}

export function topAccountDevicePath(brand: string, memberId: number, snapshotId: string, cursor?: string | null): string {
  const params = new URLSearchParams({ brand, snapshotId });
  if (cursor) params.set('cursor', cursor);
  return `/api/admin/operations/top-accounts/${encodeURIComponent(memberId)}/devices?${params}`;
}
